<div class="registration-form-wrapper">
  <div class="registration-form-card">
    <h1 class="form-title">Join the uTest Community</h1>
    <p class="form-subtitle">
      We ask for your age and country to ensure we meet privacy laws and regulations.<br />
      All fields with an asterisk (*) are mandatory.
    </p>

    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="unified-registration-form">
      <div class="form-row">
        <div class="form-group form-group-half">
          <label for="firstName" class="form-label">First Name <span class="required"
          aria-hidden="true">*</span></label>
          <input id="firstName" type="text" class="form-input" formControlName="firstName" placeholder="Jane"
            aria-describedby="firstNameError" required [attr.aria-invalid]="isInvalid('firstName')"
            [class.input-error]="isInvalid('firstName')"
            (blur)="form.get('firstName')?.markAsTouched()" autocomplete="given-name" />
          <span class="signup-error-message-group" id="firstNameError" aria-live="polite">
            @if (isInvalid('firstName')) {
              @if (form.get('firstName')?.hasError('required')) {
                <span class="signup-error-message">
                  <ut-error-icon></ut-error-icon>
                  First name is required
                </span>
              }
              @if (form.get('firstName')?.hasError('trimmedMinlength') || form.get('firstName')?.hasError('maxlength')) {
                <span class="signup-error-message">
                  <ut-error-icon></ut-error-icon>
                  First name must be between 2 and 50 characters
                </span>
              }
              @if (form.get('firstName')?.hasError('pattern')) {
                <span class="signup-error-message">
                  <ut-error-icon></ut-error-icon>
                  First name must not include numbers or special characters
                </span>
              }
            }
          </span>
        </div>
        <div class="form-group form-group-half">
          <label for="lastName" class="form-label">Last Name <span class="required" aria-hidden="true">*</span></label>
          <input id="lastName" type="text" class="form-input" formControlName="lastName" placeholder="Smith"
            aria-describedby="lastNameError" required [attr.aria-invalid]="isInvalid('lastName')"
            [class.input-error]="isInvalid('lastName')"
            (blur)="form.get('lastName')?.markAsTouched()" autocomplete="family-name" />
          <span class="signup-error-message-group" id="lastNameError" aria-live="polite">
            @if (isInvalid('lastName')) {
              @if (form.get('lastName')?.hasError('required')) {
                <span class="signup-error-message">
                  <ut-error-icon></ut-error-icon>
                  Last name is required
                </span>
              }
              @if (form.get('lastName')?.hasError('trimmedMinlength') || form.get('lastName')?.hasError('maxlength')) {
                <span class="signup-error-message">
                  <ut-error-icon></ut-error-icon>
                  Last name must be between 2 and 50 characters
                </span>
              }
              @if (form.get('lastName')?.hasError('pattern')) {
                <span class="signup-error-message">
                  <ut-error-icon></ut-error-icon>
                  Last name must not include numbers or special characters
                </span>
              }
            }
          </span>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group form-group-half">
          <label for="email" class="form-label">Email Address <span class="required" aria-hidden="true">*</span></label>
          <input id="email" type="email" class="form-input" formControlName="email" placeholder="jsmith@example.com"
            aria-describedby="emailError" required [attr.aria-invalid]="isInvalid('email')"
            [class.input-error]="isInvalid('email')"
            (blur)="form.get('email')?.markAsTouched()" autocomplete="email" />
          <span class="signup-error-message-group" id="emailError" aria-live="polite">
            @if (isInvalid('email')) {
              <span class="signup-error-message">
                <ut-error-icon></ut-error-icon>
                Enter a valid email address
              </span>
            }
          </span>
        </div>
        <div class="form-group form-group-half">
          <label for="countryId" class="form-label">Country of Residence <span class="required"
          aria-hidden="true">*</span></label>
          <select id="countryId" class="form-select" [class.placeholder-showing]="!form.get('countryId')?.value"
            aria-describedby="countryError" required [attr.aria-invalid]="isInvalid('countryId')"
            [class.input-error]="isInvalid('countryId')"
            formControlName="countryId" (blur)="form.get('countryId')?.markAsTouched()">
            <option value="" disabled>Select</option>
            @for (c of countries; track c.id) {
              <option [value]="c.id">{{ c.name }}</option>
            }
          </select>
          <span class="signup-error-message-group" id="countryError" aria-live="polite">
            @if (isInvalid('countryId')) {
              <span class="signup-error-message">
                <ut-error-icon></ut-error-icon>
                Country of residence is required
              </span>
            }
          </span>
        </div>
      </div>

      @if (states.length) {
        <div class="form-row">
          <div class="form-group form-group-half">
            <label for="stateId" class="form-label">State / Province / Region <span class="required"
                aria-hidden="true">*</span></label>
            <select id="stateId" class="form-select" [class.placeholder-showing]="!form.get('stateId')?.value"
              aria-describedby="stateError" required [attr.aria-invalid]="isInvalid('stateId')"
              [class.input-error]="isInvalid('stateId')"
              formControlName="stateId" (blur)="form.get('stateId')?.markAsTouched()">
              <option value="" disabled>Select</option>
              @for (s of states; track s.id) {
                <option [value]="s.id">{{ s.name }}</option>
              }
            </select>
            <span class="signup-error-message-group" id="stateError" aria-live="polite">
              @if (isInvalid('stateId')) {
                <span class="signup-error-message">
                  <ut-error-icon></ut-error-icon>
                  State / Province / Region is required
                </span>
              }
            </span>
          </div>
        </div>
      }

      <div class="form-row">
        <div class="form-group form-group-date">
          <label id="date-group" class="form-label">Date of Birth <span class="required"
          aria-hidden="true">*</span></label>
          <div class="date-of-birth-container">
            <div class="date-field">
              <label id="birthMonthLabel" for="birthMonth" class="form-label">Month</label>
              <select id="birthMonth" class="form-select date-select"
                [class.placeholder-showing]="!form.get('birthMonth')?.value" formControlName="birthMonth"
                [class.input-error]="isInvalid('birthMonth')"
                aria-describedby="monthError" required [attr.aria-invalid]="isInvalid('birthMonth')" aria-labelledby="date-group birthMonthLabel"
                (change)="onMonthYearChange()">
                <option value="" disabled>Select</option>
                @for (m of months; track m.id) {
                  <option [value]="m.id">{{ m.name }}</option>
                }
              </select>
              <span class="signup-error-message-group" id="monthError" aria-live="polite">
                @if (isInvalid('birthMonth')) {
                  <span class="signup-error-message">
                    <ut-error-icon></ut-error-icon>
                    Month is required
                  </span>
                }
              </span>
            </div>
            <div class="date-field">
              <label id="birthDayLabel"  for="birthDay" class="form-label">Day</label>
              <select id="birthDay" class="form-select date-select"
                [class.placeholder-showing]="!form.get('birthDay')?.value" formControlName="birthDay"
                [class.input-error]="isInvalid('birthDay')"
                aria-describedby="dayError" required [attr.aria-invalid]="isInvalid('birthDay')" aria-labelledby="date-group birthDayLabel"
                (blur)="form.get('birthDay')?.markAsTouched()">
                <option value="" disabled>Select</option>
                @for (d of days; track d) {
                  <option [value]="d">{{ d }}</option>
                }
              </select>
              <span class="signup-error-message-group" id="dayError" aria-live="polite">
                @if (isInvalid('birthDay')) {
                  <span class="signup-error-message">
                    <ut-error-icon></ut-error-icon>
                    Day is required
                  </span>
                }
              </span>
            </div>
            <div class="date-field">
              <label id="birthYearLabel"  for="birthYear" class="form-label">Year</label>
              <select id="birthYear" class="form-select date-select"
                [class.placeholder-showing]="!form.get('birthYear')?.value" formControlName="birthYear"
                [class.input-error]="isInvalid('birthYear')"
                aria-describedby="yearError" required [attr.aria-invalid]="isInvalid('birthYear')" aria-labelledby="date-group birthYearLabel"
                (change)="onMonthYearChange()">
                <option value="" disabled>Select</option>
                @for (y of years; track y) {
                  <option [value]="y">{{ y }}</option>
                }
              </select>
              <span class="signup-error-message-group" id="yearError" aria-live="polite">
                @if (isInvalid('birthYear')) {
                  <span class="signup-error-message">
                    <ut-error-icon></ut-error-icon>
                    Year is required
                  </span>
                }
              </span>
            </div>
          </div>
          <span class="signup-error-message-group" aria-live="polite">
            @if (hasAgeError()) {
              <span class="signup-error-message">
                <ut-error-icon></ut-error-icon>
                You must be 18 years or older to create an account
              </span>
            }
          </span>
        </div>
      </div>

      <div class="consent-section">
        <div class="form-group checkbox-group">
          <div class="checkbox-input-group">
            <input id="newsletterOptIn" type="checkbox" class="checkbox-input" name="newsletterOptIn" formControlName="newsletterOptIn" />
            <span class="checkbox-custom" (click)="toggleCheckbox('newsletterOptIn')"></span>
            <label class="checkbox-label" for="newsletterOptIn">
              <span class="checkbox-text">I agree to receive email updates about uTest projects and news. I
              can manage my notification settings or unsubscribe at any time.</span>
            </label>
          </div>
        </div>
        <div class="form-group checkbox-group" (focusout)="onCheckboxGroupFocusOut($event, 'termsAccepted')">
          <div class="checkbox-input-group">
            <input id="termsAccepted" type="checkbox" class="checkbox-input" name="termsAccepted"
              [checked]="form.get('termsAccepted')?.value"
              (change)="onTermsCheckboxChange($event)"
              aria-describedby="termsAcceptedError"
              required />
            <span class="checkbox-custom" [class.checkbox-custom-error]="isInvalid('termsAccepted')" (click)="toggleCheckbox('termsAccepted')"></span>
            <label class="checkbox-label" for="termsAccepted">
              <span class="checkbox-text" aria-atomic="true">I have read and agreed to the
                <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a>,
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and
                <a href="/ai-guidelines" target="_blank" rel="noopener noreferrer">AI Guidelines</a>.<span
              class="required" aria-hidden="true">*</span></span>
            </label>
          </div>
          <span class="signup-error-message-group" id="termsAcceptedError" aria-live="polite">
            @if (isInvalid('termsAccepted')) {
              <span class="signup-error-message">
                <ut-error-icon></ut-error-icon>
                You must accept the Terms of Use, Privacy Policy and AI Guidelines to be able to continue
              </span>
            }
          </span>
        </div>
      </div>

      <div class="submit-section">
        <button type="submit" class="btn-create-account" [disabled]="form.invalid || submitting">Create
        Account</button>
      </div>
    </form>
  </div>
</div>
